import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Group, ActionIcon, TextInput, Text, Button, Loader, Tooltip, Box, Divider } from "@mantine/core";
import { openSpotlight } from "@mantine/spotlight";
import { useTranslation } from "react-i18next";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";

import FilterSelectionPanel from "./FilterSelectionPanel";
import TagsChips from "../discussion/TagsChips";

import { setTiptapSidemenu } from "../../../../store/app";
import isSpaceThemed from "../../../../utils/IsSpaceThemed";
import { sutraTheme } from "../../../../collections/sutraTheme";

export default function FilterPanelController({
  type,
  section,
  filters,
  filtersSelected,
  sortSelected,
  paymentFilters,
  loading,
  colors = {},
  term,
  tagsList,
  tagsSelected,
  optionCounts,
  topLocations,
  isFilterVisible,
  setTags,
  setTerm,
  onSelectFilter,
  onUpdateFilter,
  onUpdateSort,
  setIsFilterVisible,
  onPressSettings,
  onSelectTag,
  WrapperComponent,
  LeftComponent,
  RightComponent
}) {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const user = useSelector((state) => state.user);

  const color = isSpaceThemed(colors) ? colors?.primary_button_background_color : undefined;

  const [isSearching, setIsSearching] = useState(false);
  const [isSortVisible, setIsSortVisible] = useState(false);

  const showSettings = ["admin", "manager"].includes(window.$currentRole);
  const isMemberList = section === "members-list";
  const panelFilters = Array.isArray(filters) ?
    filters.filter((f) => f.visibility.includes("panel") && (showSettings || !f.visibility.includes("managers")) && !isMemberList)
  : [];
  const otherFilters = Array.isArray(filters) ?
    panelFilters.length > 0 ?
      filters.filter((f) => !f.visibility.includes("panel") && (showSettings || !f.visibility.includes("managers")))
    : filters
  : [];

  const _onPressSettings = (e) => {
    if (typeof onPressSettings === "function") {
      onPressSettings(e);
    } else {
      dispatch(setTiptapSidemenu({ opened: type, option: window.$fetchedTopSlug }));
    }
  };

  const onKeyDown = (event) => {
    if (event.which == 13) {
      event.preventDefault();
      onSearch();
    }
  };

  const onSearch = () => {
    if (typeof setTerm === "function") {
      setTerm(document.getElementById("search-input").value);
    }
  };

  const onClearSearch = () => {
    document.getElementById("search-input").value = "";
    if (typeof setTerm === "function") {
      setTerm("");
    }
  };

  const toggleFilter = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (panelFilters.length > 0) return;
    setIsFilterVisible((prev) => !prev);
    if (isSortVisible) setIsSortVisible(false);
  };

  const toggleSort = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsSortVisible((prev) => !prev);
    if (panelFilters.length > 0) return;
    setIsFilterVisible(!(isFilterVisible && isSortVisible));
  };

  const _onPressSearch = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (section === "spaces-list") {
      openSpotlight();
    } else {
      setIsSearching(true);
    }
  };

  const Wrapper = WrapperComponent || Box;

  return (
    <>
      <Wrapper>
        <Group position="apart" mt={section === "spaces-list" || (type === "filter-members" && section !== "members-settings") ? 0 : 20}>

          {!!LeftComponent && <LeftComponent />}

          {isSearching ? (
            <Group position="left" spacing="xs">
              <ActionIcon onClick={() => setIsSearching(false)}>
                <FontAwesomeIcon icon={solid("xmark")} color={color} />
              </ActionIcon>
              <TextInput
                sx={{ width: "200px" }}
                icon={<FontAwesomeIcon icon={solid("search")} />}
                placeholder={t("spaces.settings.members.registered.registered.searchPlaceholder")}
                id="search-input"
                radius="xl"
                onKeyDown={(event) => onKeyDown(event)}
                rightSection={
                  term ? (
                    <Text
                      sx={{ cursor: "pointer" }}
                      onClick={() => onClearSearch()}
                    >
                      <FontAwesomeIcon icon={solid("xmark")} />
                    </Text>
                  ) : (
                    <Text>{"  "}</Text>
                  )
                }
              />
              <Button
                variant="filled"
                radius="xl"
                onClick={() => onSearch()}
                sx={{
                  backgroundColor: sutraTheme.colors.sutrablue[3],
                  "&:hover": {
                    backgroundColor: sutraTheme.colors.sutrablue[4],
                  }
                }}
              >
                {t("spaces.settings.members.registered.registered.searchButton")}
              </Button>
              {loading && <Loader size="xs" />}
            </Group>
          ) : (
            <Group spacing={0} pb={0}>
              {(showSettings || filters.length > 0) && (
                <>
                  <Tooltip label="Filter" withArrow>
                    <ActionIcon variant="transparent" onClick={toggleFilter}>
                      <FontAwesomeIcon icon={solid("sliders")} color={color} />
                    </ActionIcon>
                  </Tooltip>
                  {section !== "spaces-list" && (
                    <Tooltip label="Sort" withArrow>
                      <ActionIcon variant="transparent" onClick={toggleSort}>
                        <FontAwesomeIcon icon={solid("sort")} color={color} />
                      </ActionIcon>
                    </Tooltip>
                  )}
                  <Tooltip label="Search" withArrow>
                    <ActionIcon variant="transparent" onClick={_onPressSearch}>
                      <FontAwesomeIcon icon={solid("search")} color={color} />
                    </ActionIcon>
                  </Tooltip>
                </>
              )}
              {showSettings && (
                <Tooltip label={`Edit ${section === "spaces-list" ? "space" : "member"} properties`} withArrow>
                  <ActionIcon variant="transparent" onClick={_onPressSettings}>
                    <FontAwesomeIcon icon={solid("cog")} color={color} />
                  </ActionIcon>
                </Tooltip>
              )}
            </Group>
          )}

          {!!RightComponent && <RightComponent />}

        </Group>
      </Wrapper>

      {type === "filter-members" && section !== "members-settings" && (
        <Divider
          mt={10}
          mb={isFilterVisible? 10 : 25}
          sx={(theme) => ({ borderTop: `1px solid ${theme.colors.gray[2]}` })}
        />
      )}

      {(isFilterVisible || panelFilters.length > 0) && (
        <FilterSelectionPanel
          section={section}
          filters={otherFilters}
          filtersSelected={filtersSelected}
          panelFilters={panelFilters}
          isSortVisible={isSortVisible}
          sortSelected={sortSelected}
          tagsList={tagsList}
          tagsSelected={tagsSelected}
          optionCounts={optionCounts}
          topLocations={topLocations}
          paymentFilters={paymentFilters}
          onSelectFilter={onSelectFilter}
          onUpdateFilter={onUpdateFilter}
          onUpdateSort={(filter, shouldHide) => {
            if (typeof onUpdateSort === "function") {
              onUpdateSort(filter);
            }
            if (shouldHide) {
              setIsSortVisible(false);
            }
          }}
          onPressCreateFilter={_onPressSettings}
          onSelectTag={onSelectTag}
        />
      )}

      {Array.isArray(tagsList) && tagsList.length > 0 && (!isFilterVisible && panelFilters.length === 0) && (
        <TagsChips tagsList={tagsList} setTags={setTags} selectedTag={tagsSelected} />
      )}

    </>
  );
}